import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { useDragAndDrop } from '@formkit/drag-and-drop/react'
import { AddCircleOutlineOutlined, PeopleOutlined, SupportAgent } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Typography, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import BackButton from '../../components/back-button'
import ContactDialog from '../../components/contact-dialog'
import BasicDialog from '../../components/m3/basic-dialog'
import DashboardScreen from '../../components/screen/dashboard-screen'
import OnboardingScreen from '../../components/screen/onboarding-screen'
import SelectedFolderBanner from '../../components/selected-folder-banner'
import StickyHeader from '../../components/sticky-header'
import { AuthenticationContext } from '../../providers/auth-provider'
import Api from '../../utils/api'
import useIsMobile from '../../utils/hooks/useIsMobile'

import ContactCard from './contact-card'
import ContactsContext from './context'
import { onDragEnd, onReplaceFirstContact, parseNotificationPreferences } from './helpers'
import useStyles from './index.styles'

const Contacts = () => {
  const classes = useStyles
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const location = useLocation()
  const { folderId: folderIdParam, userId } = useParams<{ folderId: string; userId: string }>()
  const { folderId: folderIdState } = (location.state as { folderId?: number | string } | null) || {}
  const folderId = (folderIdParam ? Number(folderIdParam) : null) ?? (folderIdState ? Number(folderIdState) : null)
  const selectedFolderId = folderId
  const isOnboarding = location.pathname.startsWith('/onboarding')
  const hasSelectedContact = !!userId
  const isOnboardingSelectedContact = isOnboarding && hasSelectedContact

  const [searchParams] = useSearchParams()
  const action = searchParams.get('action')

  const { ownUser } = useContext(AuthenticationContext)

  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)

  const [folder, setFolder] = useState<IFolderType | null>(null)
  const [canAddUsers, setCanAddUsers] = useState<boolean>(false)
  const [openContactForm, setOpenContactForm] = useState<boolean>(action === 'add')

  const [replaceFirstContactDialogOpen, setReplaceFirstContactDialogOpen] = useState<boolean>(false)
  const [dialogButtonLoading, setDialogButtonLoading] = useState<boolean>(false)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [doNotContactContacts, setDoNotContactContacts] = useState<INotificationContact[]>([])
  const prevContactsState = useRef<INotificationContact[]>([])
  const handleDragStart = useCallback(() => {
    setIsDragging(true)
  }, [])

  const handleDragEndEvent = useCallback(
    async (props: {
      values?: INotificationContact[]
      parent?: {
        data?: { setValues?: (values: INotificationContact[], parent: HTMLElement) => void }
        el?: HTMLElement
      }
    }) => {
      const values = Array.isArray(props?.values) ? props.values : prevContactsState.current
      const setParentValues = props?.parent?.data?.setValues

      await onDragEnd({
        setIsDragging,
        setReplaceFirstContactDialogOpen,
        selectedFolderId,
        values,
        prevValues: prevContactsState,
        setParentValues: (nextValues: INotificationContact[], parent: HTMLElement) => {
          if (typeof setParentValues === 'function') {
            setParentValues(nextValues, parent)
          }
        },
        parentEl: props?.parent?.el || document.body,
        enqueueSnackbar,
      })
    },
    [enqueueSnackbar, selectedFolderId]
  )

  const [parentRef, notificationContacts, setNotificationContacts, updateDragAndDropConfig] = useDragAndDrop<HTMLDivElement, INotificationContact>([], {
    onDragstart: handleDragStart,
    onDragend: handleDragEndEvent,
  })

  const handleCloseContactForm = () => {
    setOpenContactForm(false)
    if (isOnboardingSelectedContact) navigate('/onboarding-contacts', { state: location.state })
    else if (isOnboarding) searchParams.set('action', '')
    else if (folderId) navigate(`/dashboard/${folderId}/contacts`, { state: location.state })
    else navigate('/dashboard', { state: location.state })
  }

  useEffect(() => {
    if (action === 'add') {
      setOpenContactForm(true)
    }
  }, [action])

  const onboardingEditUser = isOnboardingSelectedContact
    ? [...notificationContacts, ...doNotContactContacts].find((c) => c.user.id === Number(userId))?.user
    : undefined

  useEffect(() => {
    if (isOnboardingSelectedContact && onboardingEditUser) {
      setOpenContactForm(true)
    }
  }, [isOnboardingSelectedContact, onboardingEditUser])

  // Re-initialize drag-and-drop whenever contacts change. The library's MutationObserver
  // fires before React's useEffect updates its internal value map, so nodes won't be
  // registered as draggable after async data loads without this explicit re-initialization.
  useEffect(() => {
    if (isDragging) return
    updateDragAndDropConfig({
      onDragstart: handleDragStart,
      onDragend: handleDragEndEvent,
    })
  }, [handleDragEndEvent, handleDragStart, isDragging, notificationContacts.length, updateDragAndDropConfig])

  const getNotificationContacts = useCallback(async () => {
    if (folderId && ownUser) {
      setLoading(true)

      const resFolder = await Api.get(`/api/folders/${folderId}`)
      if (resFolder.ok) {
        setFolder(resFolder.body)
      } else {
        setError('Could not load folder information')
        setLoading(false)
        return
      }

      const res = await Api.get(`/api/v2/protect/folders/${folderId}/notification-contacts`)
      setLoading(false)
      if (res.ok) {
        // Add a doNotContact property to each contact depending on notification preferences and existence of a primary phone number
        const contacts = (res.body.users as INotificationContact[]).map((c) => {
          return {
            ...c,
            doNotContact: parseNotificationPreferences(c.user.preferences?.notification).length === 0 && !!c.user.phone,
          }
        })
        // Batch the contacts into those with notifications turned on or those with no phone (invited users) and those with notifications turned off with phone set up (do not contact), then sort by `notificationPriority`
        const newNotificationContacts = contacts.filter((c) => !c.doNotContact).sort((a, b) => ((a.priority ?? -1) > (b.priority ?? -1) ? 1 : -1))
        const newDoNotContactContacts = contacts
          .filter((c) => c.doNotContact)
          .sort((a, b) => `${a.user.information.first} ${a.user.information.last}`.localeCompare(`${b.user.information.first} ${b.user.information.last}`))
        setNotificationContacts(newNotificationContacts)
        prevContactsState.current = newNotificationContacts
        setDoNotContactContacts(newDoNotContactContacts)
        setCanAddUsers(res.body.canAddUsers as boolean)
      } else {
        setError('We could not load your notification contacts')
      }
    }
  }, [folderId, ownUser, setNotificationContacts])

  useEffect(() => {
    getNotificationContacts()
  }, [getNotificationContacts, ownUser])

  const handleSubmit = async () => {
    if (!ownUser || !folderId) return
    if (notificationContacts.length < 1) {
      enqueueSnackbar('Please select at least one notification preference for yourself or create an additional contact with active notification preferences', {
        variant: 'error',
      })
      return
    }
    try {
      setSubmitting(true)
      await Api.patch(`/api/v2/folders/${folderId}/attributes`, {
        stepContactInformationUser: ownUser.id,
      })
      // Save notifyPriority
      await Api.post(`/api/v2/protect/folders/${folderId}/notification-priority`, {
        userIds: notificationContacts.map((c) => c.user.id),
      })
      enqueueSnackbar('Notification contacts saved successfully')
      navigate(`/dashboard/${folderId}/devices`)
    } catch {
      enqueueSnackbar('There was a problem setting up your account.', { variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const Container = isOnboarding ? OnboardingScreen : DashboardScreen

  return (
    <ContactsContext.Provider value={{ getNotificationContacts, notificationContacts, doNotContactContacts }}>
      <Container
        loading={Boolean(folderId) && loading}
        error={error}
        {...(!isOnboarding && {
          outletAlignStart: hasSelectedContact,
          shouldConstrainWidth: true,
        })}
        headerProps={
          isOnboarding
            ? { title: 'Setup', showUserMenu: true, leftIcon: <BackButton /> }
            : {
                showLogo: true,
                rightIconMobile: (
                  <IconButton
                    size="small"
                    edge="end"
                    color="secondary"
                    aria-label="Add Contact"
                    onClick={() => navigate(folderId ? `/dashboard/${folderId}/contacts?action=add` : '/dashboard')}
                  >
                    <AddCircleOutlineOutlined />
                  </IconButton>
                ),
              }
        }
        footer={
          isOnboarding && (
            <Box sx={classes.footer}>
              <Grid container={true} size={12} sx={classes.footerButtonContainer}>
                <Button
                  variant="contained"
                  disableElevation
                  color="secondary"
                  startIcon={<AddCircleOutlineOutlined />}
                  onClick={() => setOpenContactForm(true)}
                >
                  Add contact
                </Button>

                <Button variant="contained" disableElevation color="primary" disabled={submitting} loading={submitting} onClick={handleSubmit}>
                  I'm done
                </Button>
              </Grid>
            </Box>
          )
        }
      >
        {isOnboarding && (
          <Grid container={true} sx={classes.stickyBanner}>
            <SelectedFolderBanner folder={folder} isActivation />
          </Grid>
        )}

        <Grid container={true} spacing={2} sx={classes.contactsList(isOnboarding)}>
          {!isOnboarding && isMobile && <SelectedFolderBanner folder={folder} />}

          {!isMobile && !isOnboarding && (
            <StickyHeader marginBottom={1} sx={classes.stickyHeader}>
              <Grid container={true} size={12} sx={classes.contactsHeaderRow((ownUser?.folders.length || 0) > 0, isMobile, isOnboarding)}>
                <Typography variant="body2" sx={classes.contactsHeader}>
                  <PeopleOutlined /> Contacts {notificationContacts.length > 0 && `${notificationContacts.length}`}
                </Typography>
                {!isMobile && !isOnboarding && (
                  <IconButton color="primary" onClick={() => setOpenContactForm(true)} aria-label="Add Contact">
                    <AddCircleOutlineOutlined />
                  </IconButton>
                )}
              </Grid>
            </StickyHeader>
          )}

          {isMobile && isOnboarding && (
            <Grid container={true} size={12}>
              <Grid container={true} size={12} sx={classes.contactsHeaderRow((ownUser?.folders.length || 0) > 0, isMobile, isOnboarding)}>
                <Typography variant="body2" sx={classes.contactsHeader}>
                  <PeopleOutlined /> Contacts {notificationContacts.length > 0 && `${notificationContacts.length}`}
                </Typography>
              </Grid>
            </Grid>
          )}

          {isOnboarding && (
            <Grid size={12}>
              <Typography variant="h6" color="textSecondary">
                Your Notification Contacts
              </Typography>
            </Grid>
          )}

          <Typography variant="body2" color="textSecondary" sx={classes.contactsDescription}>
            Add contacts to be notified during an alert. Drag to reorder based on priority.
          </Typography>

          {ownUser && (
            <Grid size={12}>
              <Box ref={parentRef} sx={classes.contactsListBox}>
                {notificationContacts.map((contact, idx) => {
                  return (
                    <ContactCard
                      key={contact.user.id}
                      user={contact.user}
                      idx={idx}
                      isOwnUser={contact.user.id === ownUser.id}
                      isDragging={isDragging}
                      canAddUsers={canAddUsers}
                      isOnboarding={isOnboarding}
                    />
                  )
                })}
              </Box>

              {doNotContactContacts.map((contact, idx) => {
                return (
                  <ContactCard
                    key={contact.user.id}
                    user={contact.user}
                    idx={idx}
                    isOwnUser={contact.user.id === ownUser.id}
                    isDragging={isDragging}
                    canAddUsers={canAddUsers}
                    isOnboarding={isOnboarding}
                  />
                )
              })}

              {folderId !== null && (
                <ContactDialog
                  selectedFolderId={folderId}
                  openContactForm={openContactForm}
                  handleCloseContactForm={handleCloseContactForm}
                  reload={getNotificationContacts}
                  allContacts={notificationContacts}
                  editUser={onboardingEditUser}
                />
              )}

              <BasicDialog
                title="Replace First Contact"
                icon={<SupportAgent htmlColor={theme.palette.text.secondary} />}
                textContent="You are about to replace the 24 hour call line from the first contact position."
                open={replaceFirstContactDialogOpen}
                onClose={() => {
                  // Restore last sort and close the dialog
                  setNotificationContacts(prevContactsState.current)
                  setReplaceFirstContactDialogOpen(false)
                }}
                onConfirm={async () => {
                  onReplaceFirstContact({
                    selectedFolderId,
                    notificationContacts,
                    prevContacts: prevContactsState,
                    setNotificationContacts,
                    setReplaceFirstContactDialogOpen,
                    setDialogButtonLoading,
                    enqueueSnackbar,
                  })
                }}
                confirmLabel="Replace"
                confirmLoading={dialogButtonLoading}
                showCloseIcon
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </ContactsContext.Provider>
  )
}

export default Contacts
